import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Filter,
  FileText,
  Clock,
  Users,
  ExternalLink,
  MessageCircle,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyTeacherProfile } from "../../services/teachers";

const TeacherCourses: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;

  // Fetch teacher profile to get assigned courses
  const { data: teacherData, isLoading } = useQuery({
    queryKey: ["teacher-profile"],
    queryFn: () => getMyTeacherProfile(),
    enabled: !!user?.id,
  });

  const teacher = teacherData?.data;

  // Extract unique courses from all class assignments
  const teacherCourses = React.useMemo(() => {
    const coursesMap = new Map();

    teacher?.currentClasses?.forEach((classAssignment: any) => {
      classAssignment.courses?.forEach((course: any) => {
        if (!coursesMap.has(course._id)) {
          coursesMap.set(course._id, {
            ...course,
            className: classAssignment.classId.name,
            classId: classAssignment.classId._id,
          });
        }
      });
    });

    return Array.from(coursesMap.values());
  }, [teacher?.currentClasses]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Your Assigned Courses
            </h1>
            <p className="text-slate-600 mt-1 font-medium">
              Manage and view your teaching courses
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <div className="relative flex-1 lg:flex-initial">
              <input
                type="text"
                placeholder="Search courses..."
                className="w-full pl-12 pr-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
              />
            </div>
            <button className="flex items-center justify-center gap-2 bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
              <Filter size={18} />
              Filter
            </button>
          </div>
        </div>
      </div>

      {teacherCourses.length === 0 ? (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12 text-center">
          <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 w-16 h-16 mx-auto shadow-lg flex items-center justify-center mb-4">
            <BookOpen className="text-white" size={32} />
          </div>
          <h3 className="mt-2 text-lg font-bold text-slate-900">
            No courses assigned yet
          </h3>
          <p className="mt-1 text-sm text-slate-600 font-medium">
            Contact your administrator to get assigned to courses.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teacherCourses.map((course) => (
            <div
              key={course._id}
              className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 min-w-0 mr-3">
                    <h2 className="text-lg font-bold text-slate-900 mb-2 break-words">
                      {course.name}({course.level})
                    </h2>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-3 py-1 rounded-xl text-xs font-bold shadow-sm">
                        {course.level || course.category}
                      </span>
                      <span className="bg-gradient-to-br from-emerald-300 to-emerald-400 text-white px-3 py-1 rounded-xl text-xs font-bold shadow-sm">
                        {course.credits} credits
                      </span>
                    </div>
                    <p className="text-slate-600 text-sm font-semibold break-words">
                      Class: {course.className}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-xl p-2.5 shadow-lg flex-shrink-0">
                    <BookOpen className="text-white" size={24} />
                  </div>
                </div>

                {/* Course Info */}
                <div className="space-y-3 mb-5">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-amber-300 to-yellow-400 rounded-lg p-1.5 shadow-sm">
                      <Clock size={16} className="text-white" />
                    </div>
                    <span className="text-slate-600 text-sm font-medium">
                      {typeof course.duration === "object"
                        ? course.duration.totalHours
                        : "45"}{" "}
                      hours
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Link
                    to={`/teacher/courses/${course._id}`}
                    className="flex items-center justify-center gap-2 flex-1 bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-4 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    <ExternalLink size={16} />
                    View Course
                  </Link>
                  {course.whatsappGroupLink && (
                    <a
                      href={course.whatsappGroupLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-gradient-to-br from-green-300 to-emerald-400 text-white px-4 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
                    >
                      <MessageCircle size={16} />
                      WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeacherCourses;
